<template>
  <div class="landing-page">
    <!-- ===================== HEADER ===================== -->
    <header class="header" :class="{ 'header--scrolled': isScrolled }">
      <div class="header-container">
        <nav class="nav" aria-label="Navegação principal">
          <!-- Logo -->
          <a
            class="logo"
            href="https://intelite.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Intelite Tecnologia"
          >
            <img
              src="@/assets/landing/logos/logo-intelite.png"
              alt="Intelite Tecnologia"
              class="logo-image"
            />
          </a>

          <!-- Menu Desktop -->
          <div class="nav-desktop">
            <button
              v-for="item in menuItems"
              :key="item.id"
              @click="scrollToSection(item.id)"
              class="nav-button"
              :class="{ active: activeSection === item.id }"
            >
              {{ item.label }}
              <span class="nav-underline" :class="{ active: activeSection === item.id }"></span>
            </button>
          </div>

          <!-- CTA + Login Desktop -->
          <div class="nav-actions">
            <a
              :href="whatsappLink"
              target="_blank"
              rel="noopener noreferrer"
              class="cta-button cta-button--ghost"
            >
              Solicitar demonstração
            </a>
            <button @click="goToLogin" class="login-button">
              <svg class="login-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Login
            </button>
          </div>

          <!-- Menu Mobile Button -->
          <button @click="toggleMenu" class="menu-toggle" aria-label="Abrir menu">
            <svg class="menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!isMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </nav>
      </div>

      <!-- Overlay + Menu Mobile -->
      <div v-if="isMenuOpen" class="overlay" @click="toggleMenu"></div>
      <div class="nav-mobile" :class="{ open: isMenuOpen }">
        <div class="nav-mobile-content">
          <button
            v-for="item in menuItems"
            :key="item.id"
            @click="scrollToSection(item.id)"
            class="nav-button-mobile"
            :class="{ active: activeSection === item.id }"
          >
            {{ item.label }}
          </button>
          <a :href="whatsappLink" target="_blank" rel="noopener noreferrer" class="cta-button cta-button--solid mobile">
            Solicitar demonstração
          </a>
          <button @click="goToLogin" class="login-button-mobile">
            <svg class="login-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            Login
          </button>
        </div>
      </div>
    </header>

    <main class="main">
      <!-- ===================== HERO ===================== -->
      <section id="home" class="hero">
        <img src="@/assets/landing/banners/BG.png" alt="" class="hero-bg" aria-hidden="true" />
        <div class="hero-overlay" aria-hidden="true"></div>
        <div class="hero-content">
          <img
            src="@/assets/landing/logos/logo_ainurse.png"
            alt="AiNurse"
            class="hero-logo animate-fade-in"
          />
          <h1 class="hero-title animate-slide-up">
            Chamada de enfermagem inteligente para hospitais e clínicas
          </h1>
          <p class="hero-subtitle animate-slide-up-delay">
            O AiNurse automatiza o chamado de leitos, acelera o atendimento da sua equipe e
            registra cada chamada com segurança — pronto para auditorias e certificações de qualidade.
          </p>
          <div class="hero-actions animate-slide-up-delay">
            <a :href="whatsappLink" target="_blank" rel="noopener noreferrer" class="cta-button cta-button--solid">
              <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
              </svg>
              Falar com especialista
            </a>
            <button @click="scrollToSection('como-funciona')" class="cta-button cta-button--outline">
              Como funciona
            </button>
          </div>
        </div>
        <div class="hero-scroll" aria-hidden="true">
          <span></span>
        </div>
      </section>

      <!-- ===================== TRUST BAR ===================== -->
      <section class="stats">
        <div class="stats-grid">
          <div class="stat animate-fade-in" v-for="(s, i) in stats" :key="i">
            <div class="stat-value">{{ s.value }}</div>
            <div class="stat-label">{{ s.label }}</div>
          </div>
        </div>
      </section>

      <!-- ===================== SOBRE / PRODUTO ===================== -->
      <section id="sobre" class="sobre">
        <div class="container sobre-grid">
          <div class="sobre-text animate-slide-up">
            <span class="eyebrow">O que é o AiNurse</span>
            <h2>Da chamada do leito ao registro auditável, em um só sistema</h2>
            <p>
              O AiNurse é a solução da Intelite para chamada de leitos em ambientes de saúde.
              Com botões de acionamento no leito, painéis nos postos de enfermagem e registro
              automático de cada atendimento, o sistema reduz o tempo de resposta da equipe e
              garante rastreabilidade total — um requisito essencial para certificações de
              qualidade hospitalar.
            </p>
            <ul class="sobre-list">
              <li v-for="(item, i) in sobreItems" :key="i">
                <span class="check" aria-hidden="true"></span>{{ item }}
              </li>
            </ul>
          </div>
          <div class="sobre-media animate-scale-in">
            <img src="@/assets/landing/banners/banner2.png" alt="Dispositivo chamador de leito AiNurse" />
          </div>
        </div>
      </section>

      <!-- ===================== COMO FUNCIONA ===================== -->
      <section id="como-funciona" class="como">
        <div class="container">
          <div class="section-head animate-fade-in">
            <span class="eyebrow">Como funciona</span>
            <h2>Simples para o paciente, completo para a gestão</h2>
            <p class="section-sub">Três passos entre o chamado e o registro auditável.</p>
          </div>
          <div class="steps">
            <div class="step animate-slide-up" v-for="(step, i) in steps" :key="i">
              <div class="step-number">{{ i + 1 }}</div>
              <div class="step-icon" v-html="step.icon"></div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ===================== VANTAGENS ===================== -->
      <section id="vantagens" class="vantagens">
        <div class="container">
          <div class="section-head animate-fade-in">
            <span class="eyebrow">Vantagens</span>
            <h2>Por que hospitais escolhem o AiNurse</h2>
            <p class="section-sub">Tecnologia que melhora o atendimento e dá tranquilidade à gestão.</p>
          </div>
          <div class="cards-grid">
            <div class="card animate-slide-up" v-for="(v, i) in vantagens" :key="i">
              <div class="card-icon" v-html="v.icon"></div>
              <h3>{{ v.title }}</h3>
              <p>{{ v.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ===================== PARA QUEM ===================== -->
      <section class="para-quem">
        <div class="container">
          <div class="section-head animate-fade-in">
            <span class="eyebrow eyebrow--light">Para quem é</span>
            <h2>Feito para todo ambiente de cuidado</h2>
          </div>
          <div class="pills">
            <span class="pill animate-fade-in" v-for="(p, i) in paraQuem" :key="i">{{ p }}</span>
          </div>
        </div>
      </section>

      <!-- ===================== CLIENTES ===================== -->
      <section id="clientes" class="clientes">
        <div class="container">
          <div class="section-head animate-fade-in">
            <span class="eyebrow">Clientes</span>
            <h2>Instituições que confiam na Intelite</h2>
            <p class="section-sub">Hospitais e clínicas que já usam o AiNurse no dia a dia.</p>
          </div>
          <div class="clientes-grid" :class="{ 'clientes-grid--single': clientes.length === 1 }">
            <div class="cliente-logo animate-fade-in" v-for="(cliente, i) in clientes" :key="i">
              <img :src="cliente.img" :alt="cliente.name" class="cliente-logo-image" />
            </div>
          </div>
        </div>
      </section>

      <!-- ===================== CTA FINAL ===================== -->
      <section class="cta-final">
        <div class="container cta-final-inner animate-fade-in">
          <h2>Pronto para acelerar o atendimento da sua equipe?</h2>
          <p>Fale com a Intelite e agende uma demonstração gratuita do AiNurse.</p>
          <a :href="whatsappLink" target="_blank" rel="noopener noreferrer" class="cta-button cta-button--solid cta-button--lg">
            <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
            Fale conosco
          </a>
        </div>
      </section>
    </main>

    <!-- ===================== FOOTER ===================== -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-top">
          <div class="footer-brand">
            <img src="@/assets/landing/logos/logo-intelite.png" alt="Intelite Tecnologia" class="footer-logo-image" />
            <p class="footer-tagline">
              Soluções tecnológicas para a saúde. O AiNurse é um produto Intelite Tecnologia.
            </p>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">Navegação</h3>
            <div class="footer-links">
              <button v-for="item in menuItems" :key="item.id" @click="scrollToSection(item.id)" class="footer-nav-link">
                {{ item.label }}
              </button>
            </div>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">Contato</h3>
            <div class="footer-contacts">
              <a :href="whatsappLink" target="_blank" rel="noopener noreferrer" class="footer-link">
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" /></svg>
                (83) 3512-5222
              </a>
              <a href="mailto:comercial@intelite.com.br" class="footer-link">
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                comercial@intelite.com.br
              </a>
              <div class="footer-link footer-link--static">
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                <span>R. Carteiro Olivio Pontes, 575 — Água Fria, João Pessoa - PB</span>
              </div>
            </div>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">Redes sociais</h3>
            <div class="footer-social">
              <a href="https://www.instagram.com/intelite_tecnologia/" target="_blank" rel="noopener noreferrer" class="footer-social-link" aria-label="Instagram">
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
              </a>
              <a href="https://www.linkedin.com/company/intelite-tecnologia/" target="_blank" rel="noopener noreferrer" class="footer-social-link" aria-label="LinkedIn">
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
            </div>
          </div>
        </div>

        <div class="footer-copyright">
          <p>&copy; {{ currentYear }} Intelite Tecnologia. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import hospitalLaureano from '@/assets/landing/clientes/hospital-laureano.png'

export default {
  name: 'Site',
  data() {
    return {
      clientes: [
        { name: 'Hospital Napoleão Laureano', img: hospitalLaureano }
      ],
      isMenuOpen: false,
      isScrolled: false,
      activeSection: 'home',
      isScrolling: false,
      observer: null,
      whatsappLink: 'https://wa.me/558335125222?text=Olá! Gostaria de saber mais sobre o AiNurse.',
      menuItems: [
        { id: 'home', label: 'Início' },
        { id: 'sobre', label: 'Sobre' },
        { id: 'como-funciona', label: 'Como funciona' },
        { id: 'vantagens', label: 'Vantagens' },
        { id: 'clientes', label: 'Clientes' },
        { id: 'contato', label: 'Contato' }
      ],
      stats: [
        { value: 'Tempo real', label: 'Chamados exibidos na hora nos painéis' },
        { value: '100% auditável', label: 'Início e atendimento de cada chamada' },
        { value: 'Multiunidade', label: 'Vários hospitais e setores em um só painel' },
        { value: 'Suporte Intelite', label: 'Implantação e acompanhamento dedicados' }
      ],
      sobreItems: [
        'Botões de chamada instalados no leito',
        'Painéis com alerta visual e sonoro nos postos de enfermagem',
        'Registro automático de cada atendimento',
        'Histórico pronto para auditorias e certificações'
      ],
      steps: [
        {
          title: 'O paciente aciona',
          text: 'Com um toque no chamador instalado no leito, o paciente solicita atendimento de forma simples e imediata.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none"/></svg>'
        },
        {
          title: 'A equipe recebe na hora',
          text: 'O chamado aparece em tempo real nos painéis do posto de enfermagem, com alerta visual e sonoro.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>'
        },
        {
          title: 'Tudo fica registrado',
          text: 'O sistema grava o horário de início e de atendimento de cada chamada, gerando um histórico auditável.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>'
        }
      ],
      vantagens: [
        {
          title: 'Atendimento mais rápido',
          text: 'Reduza o tempo de resposta da equipe com alertas instantâneos a cada chamada.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>'
        },
        {
          title: 'Registro auditável',
          text: 'Cada chamada com horário de início e atendimento, pronta para certificações de qualidade.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>'
        },
        {
          title: 'Painéis personalizáveis',
          text: 'Cores, vídeo e áudio configuráveis por unidade, exibindo informações do seu jeito.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>'
        },
        {
          title: 'Tempo real de verdade',
          text: 'Comunicação instantânea entre leitos e postos via tecnologia em tempo real.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>'
        },
        {
          title: 'Indicadores e relatórios',
          text: 'Acompanhe volume de chamados e desempenho da equipe para decisões melhores.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="6"/><rect x="12" y="7" width="3" height="10"/><rect x="17" y="13" width="3" height="4"/></svg>'
        },
        {
          title: 'Gestão multiunidade',
          text: 'Gerencie vários hospitais, setores e leitos com controle de acesso por perfil.',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 21V7l9-4 9 4v14"/><path d="M9 21v-6h6v6"/><path d="M9 10h.01M15 10h.01"/></svg>'
        }
      ],
      paraQuem: ['Hospitais', 'Clínicas e day-clinics', 'Prontos-socorros', 'Casas de repouso / ILPIs', 'Maternidades', 'Centros de diagnóstico']
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
    this.initScrollAnimations()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    onScroll() {
      this.isScrolled = window.pageYOffset > 20
      this.updateActiveSection()
    },
    initScrollAnimations() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const animated = () => document.querySelectorAll('[class*="animate-"]')
      if (reduce) {
        this.$nextTick(() => animated().forEach(el => el.classList.add('animate-visible')))
        return
      }
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-visible')
              this.observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      )
      this.$nextTick(() => animated().forEach(el => this.observer.observe(el)))
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    goToLogin() {
      this.$router.push('/login')
    },
    scrollToSection(sectionId) {
      this.activeSection = sectionId
      this.isMenuOpen = false
      this.isScrolling = true

      const target =
        sectionId === 'contato'
          ? document.querySelector('.footer')
          : document.getElementById(sectionId)
      if (!target) {
        this.isScrolling = false
        return
      }
      const headerHeight = 86
      this.smoothScrollTo(target.offsetTop - headerHeight)
    },
    smoothScrollTo(targetPosition) {
      const startPosition = window.pageYOffset
      const distance = targetPosition - startPosition
      const duration = 700
      let start = null
      const step = currentTime => {
        if (start === null) start = currentTime
        const elapsed = currentTime - start
        window.scrollTo(0, this.easeInOutQuad(elapsed, startPosition, distance, duration))
        if (elapsed < duration) requestAnimationFrame(step)
        else this.isScrolling = false
      }
      requestAnimationFrame(step)
    },
    easeInOutQuad(t, b, c, d) {
      t /= d / 2
      if (t < 1) return (c / 2) * t * t + b
      t--
      return (-c / 2) * (t * (t - 2) - 1) + b
    },
    updateActiveSection() {
      if (this.isScrolling) return
      const sections = ['home', 'sobre', 'como-funciona', 'vantagens', 'clientes']
      const headerHeight = 110
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (!element) continue
        const rect = element.getBoundingClientRect()
        if (rect.top <= headerHeight && rect.bottom > headerHeight) {
          if (this.activeSection !== sectionId) this.activeSection = sectionId
          return
        }
      }
      const footer = document.querySelector('.footer')
      if (footer) {
        const rect = footer.getBoundingClientRect()
        if (rect.top <= headerHeight && rect.bottom > headerHeight) {
          if (this.activeSection !== 'contato') this.activeSection = 'contato'
        }
      }
    }
  }
}
</script>

<style scoped>
/* ===================== TOKENS ===================== */
.landing-page {
  --navy: #0f0f41;
  --indigo: #2b3674;
  --teal: #038b8b;
  --teal-dark: #026a6a;
  --ink: #1f2433;
  --muted: #5b6478;
  --line: #e6e9f0;
  --bg-soft: #f5f7fb;
  font-family: 'Inter', 'Segoe UI', 'Roboto', Arial, sans-serif;
  color: var(--ink);
  line-height: 1.6;
  overflow-x: hidden;
  width: 100%;
}

.landing-page *,
.landing-page *::before,
.landing-page *::after {
  box-sizing: border-box;
}

.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===================== HEADER ===================== */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 86px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(140%) blur(8px);
  z-index: 50;
  border-top: 3px solid var(--navy);
  transition: box-shadow 0.3s ease, height 0.3s ease;
}
.header--scrolled {
  box-shadow: 0 6px 24px rgba(15, 15, 65, 0.08);
}
.header-container {
  max-width: 1180px;
  margin: 0 auto;
  height: 100%;
  padding: 0 24px;
}
.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  gap: 24px;
}
.logo {
  display: block;
  flex-shrink: 0;
}
.logo-image {
  height: 34px;
  width: auto;
  display: block;
  transition: opacity 0.3s ease;
}
.logo:hover .logo-image {
  opacity: 0.8;
}
.nav-desktop {
  display: flex;
  align-items: center;
  gap: 34px;
  margin-left: auto;
}
.nav-button {
  position: relative;
  background: none;
  border: none;
  color: var(--ink);
  font-weight: 600;
  font-size: 15px;
  padding: 6px 0;
  cursor: pointer;
  transition: color 0.25s ease;
}
.nav-button:hover {
  color: var(--teal);
}
.nav-button.active {
  color: var(--indigo);
}
.nav-underline {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 2px;
  background: var(--teal);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.25s ease;
}
.nav-underline.active {
  transform: scaleX(1);
}
.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.login-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--navy);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.2s ease;
}
.login-button:hover {
  background: #1a1a5c;
  transform: translateY(-1px);
}
.login-icon {
  width: 16px;
  height: 16px;
}

/* CTA buttons (shared) */
.cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease, color 0.25s ease;
  white-space: nowrap;
}
.cta-button--ghost {
  padding: 10px 16px;
  background: transparent;
  color: var(--teal);
  border: 1.5px solid var(--teal);
  font-size: 14px;
}
.cta-button--ghost:hover {
  background: var(--teal);
  color: #fff;
}
.cta-button--solid {
  padding: 14px 26px;
  background: var(--teal);
  color: #fff;
  box-shadow: 0 8px 20px rgba(3, 139, 139, 0.28);
}
.cta-button--solid:hover {
  background: var(--teal-dark);
  transform: translateY(-2px);
  color: #fff;
}
.cta-button--outline {
  padding: 14px 26px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
}
.cta-button--outline:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: #fff;
}
.cta-button--lg {
  padding: 16px 34px;
  font-size: 16px;
}
.whatsapp-icon {
  width: 20px;
  height: 20px;
}

/* Mobile menu */
.menu-toggle {
  display: none;
  background: none;
  border: none;
  color: var(--navy);
  cursor: pointer;
}
.menu-icon {
  width: 30px;
  height: 30px;
}
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 30;
}
.nav-mobile {
  position: fixed;
  top: 86px;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  z-index: 40;
  transform: translateY(-110%);
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
}
.nav-mobile.open {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}
.nav-mobile-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 32px 24px 40px;
}
.nav-button-mobile {
  background: none;
  border: none;
  color: var(--ink);
  font-weight: 600;
  font-size: 18px;
  cursor: pointer;
}
.nav-button-mobile.active {
  color: var(--indigo);
}
.cta-button--solid.mobile {
  width: 100%;
  max-width: 320px;
}
.login-button-mobile {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--navy);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  justify-content: center;
}

/* ===================== MAIN ===================== */
.main {
  margin-top: 86px;
}

/* ===================== HERO ===================== */
.hero {
  position: relative;
  min-height: 620px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}
.hero-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(
    115deg,
    rgba(15, 15, 65, 0.92) 0%,
    rgba(15, 15, 65, 0.78) 40%,
    rgba(3, 139, 139, 0.62) 100%
  );
}
.hero-content {
  position: relative;
  z-index: 3;
  max-width: 860px;
  padding: 120px 24px 90px;
  text-align: center;
  color: #fff;
}
.hero-logo {
  width: 190px;
  max-width: 60vw;
  height: auto;
  margin: 0 auto 28px;
  display: block;
}
.hero-title {
  font-size: clamp(2rem, 4.6vw, 3.3rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.01em;
  margin: 0 0 20px;
}
.hero-subtitle {
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  color: rgba(255, 255, 255, 0.9);
  max-width: 660px;
  margin: 0 auto 36px;
}
.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.hero-scroll {
  position: absolute;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  width: 26px;
  height: 42px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
}
.hero-scroll span {
  position: absolute;
  top: 8px;
  left: 50%;
  width: 4px;
  height: 8px;
  margin-left: -2px;
  background: #fff;
  border-radius: 2px;
  animation: scrollDot 1.6s ease-in-out infinite;
}
@keyframes scrollDot {
  0% { opacity: 0; transform: translateY(0); }
  40% { opacity: 1; }
  100% { opacity: 0; transform: translateY(14px); }
}

/* ===================== STATS ===================== */
.stats {
  background: var(--navy);
  padding: 38px 24px;
}
.stats-grid {
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.stat {
  text-align: center;
  color: #fff;
  padding: 6px 8px;
}
.stat + .stat {
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}
.stat-value {
  font-size: 1.35rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 6px;
}
.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
}

/* ===================== SECTION HEAD ===================== */
.eyebrow {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--teal);
  margin-bottom: 14px;
}
.eyebrow--light {
  color: rgba(255, 255, 255, 0.85);
}
.section-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 52px;
}
.section-head h2 {
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 800;
  color: var(--navy);
  line-height: 1.18;
  margin: 0 0 14px;
}
.section-sub {
  font-size: 1.05rem;
  color: var(--muted);
  margin: 0;
}

/* ===================== SOBRE ===================== */
.sobre {
  padding: 96px 0;
  background: #fff;
}
.sobre-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;
}
.sobre-text h2 {
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 800;
  color: var(--navy);
  line-height: 1.2;
  margin: 0 0 20px;
}
.sobre-text p {
  color: var(--muted);
  font-size: 1.05rem;
  margin: 0 0 24px;
}
.sobre-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}
.sobre-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: var(--ink);
}
.check {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: var(--teal);
  -webkit-mask: url('~@/assets/landing/images/Check.png') center / contain no-repeat;
  mask: url('~@/assets/landing/images/Check.png') center / contain no-repeat;
}
.sobre-media {
  position: relative;
}
.sobre-media img {
  width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 30px 60px rgba(15, 15, 65, 0.18);
}

/* ===================== COMO FUNCIONA ===================== */
.como {
  padding: 96px 0;
  background: var(--bg-soft);
}
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}
.step {
  position: relative;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 40px 28px 32px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.step:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 40px rgba(15, 15, 65, 0.1);
}
.step-number {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--navy);
  color: #fff;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}
.step-icon {
  width: 56px;
  height: 56px;
  margin: 8px auto 18px;
  color: var(--teal);
}
.step-icon >>> svg,
.card-icon >>> svg {
  width: 100%;
  height: 100%;
}
.step h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--navy);
  margin: 0 0 10px;
}
.step p {
  color: var(--muted);
  font-size: 0.98rem;
  margin: 0;
}

/* ===================== VANTAGENS ===================== */
.vantagens {
  padding: 96px 0;
  background: #fff;
}
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 26px;
}
.card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 32px 28px;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}
.card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 44px rgba(15, 15, 65, 0.1);
  border-color: transparent;
}
.card-icon {
  width: 50px;
  height: 50px;
  margin-bottom: 18px;
  color: var(--teal);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(3, 139, 139, 0.1);
  border-radius: 12px;
  padding: 11px;
}
.card h3 {
  font-size: 1.18rem;
  font-weight: 700;
  color: var(--navy);
  margin: 0 0 10px;
}
.card p {
  color: var(--muted);
  font-size: 0.98rem;
  margin: 0;
}

/* ===================== PARA QUEM ===================== */
.para-quem {
  padding: 84px 0;
  background: linear-gradient(120deg, var(--navy) 0%, var(--indigo) 100%);
}
.para-quem .section-head h2 {
  color: #fff;
}
.pills {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
}
.pill {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 1rem;
}

/* ===================== CLIENTES ===================== */
.clientes {
  padding: 96px 0;
  background: var(--bg-soft);
}
.clientes-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}
.clientes-grid--single {
  grid-template-columns: minmax(260px, 360px);
  justify-content: center;
}
.cliente-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  padding: 24px 32px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(15, 15, 65, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.cliente-logo:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 40px rgba(15, 15, 65, 0.12);
}
.cliente-logo-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.cliente-logo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px dashed #cdd3e0;
  border-radius: 12px;
  color: #aeb6c8;
  font-weight: 700;
  font-size: 0.95rem;
}

/* ===================== CTA FINAL ===================== */
.cta-final {
  padding: 90px 0;
  background: linear-gradient(120deg, var(--teal) 0%, var(--teal-dark) 100%);
}
.cta-final-inner {
  text-align: center;
  color: #fff;
}
.cta-final-inner h2 {
  font-size: clamp(1.7rem, 3.2vw, 2.5rem);
  font-weight: 800;
  margin: 0 0 14px;
}
.cta-final-inner p {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.92);
  margin: 0 0 30px;
}
.cta-final .cta-button--solid {
  background: #fff;
  color: var(--teal-dark);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
}
.cta-final .cta-button--solid:hover {
  background: #f0fbfb;
  color: var(--teal-dark);
}

/* ===================== FOOTER ===================== */
.footer {
  background: var(--navy);
  color: #fff;
  padding: 64px 0 32px;
}
.footer-container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}
.footer-top {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.3fr 1fr;
  gap: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.footer-logo-image {
  height: 34px;
  margin-bottom: 16px;
  filter: brightness(0) invert(1);
}
.footer-tagline {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  margin: 0;
  max-width: 280px;
}
.footer-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 18px;
  color: #fff;
}
.footer-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}
.footer-nav-link {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.25s ease;
}
.footer-nav-link:hover {
  color: #fff;
}
.footer-contacts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.footer-link {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.78);
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.25s ease;
}
.footer-link:hover {
  color: #fff;
}
.footer-link--static {
  cursor: default;
}
.footer-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: var(--teal);
}
.footer-social {
  display: flex;
  gap: 14px;
}
.footer-social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  transition: background 0.25s ease;
}
.footer-social-link:hover {
  background: var(--teal);
}
.footer-social-link .footer-icon {
  color: #fff;
}
.footer-copyright {
  padding-top: 28px;
  text-align: center;
}
.footer-copyright p {
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

/* ===================== ANIMATIONS ===================== */
.animate-fade-in,
.animate-slide-up,
.animate-slide-up-delay,
.animate-scale-in {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s ease, transform 0.7s ease;
  will-change: opacity, transform;
}
.animate-scale-in {
  transform: scale(0.94);
}
.animate-slide-up-delay {
  transition-delay: 0.15s;
}
.animate-visible {
  opacity: 1 !important;
  transform: none !important;
}

@media (prefers-reduced-motion: reduce) {
  .animate-fade-in,
  .animate-slide-up,
  .animate-slide-up-delay,
  .animate-scale-in {
    opacity: 1;
    transform: none;
    transition: none;
  }
  .hero-scroll span {
    animation: none;
  }
}

/* ===================== RESPONSIVE ===================== */
@media (max-width: 1024px) {
  .footer-top {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
}
@media (max-width: 900px) {
  .nav-desktop,
  .nav-actions {
    display: none;
  }
  .menu-toggle {
    display: block;
  }
  .sobre-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .sobre-media {
    order: -1;
    max-width: 480px;
    margin: 0 auto;
  }
  .steps,
  .cards-grid {
    grid-template-columns: 1fr 1fr;
  }
  .stats-grid {
    grid-template-columns: 1fr 1fr;
    gap: 28px 16px;
  }
  .stat:nth-child(odd) {
    border-left: none;
  }
}
@media (max-width: 768px) {
  .header,
  .nav-mobile {
    height: 70px;
  }
  .nav-mobile {
    top: 70px;
    height: auto;
  }
  .main {
    margin-top: 70px;
  }
  .hero {
    min-height: 540px;
  }
  .hero-content {
    padding: 96px 22px 80px;
  }
  .sobre,
  .como,
  .vantagens,
  .clientes {
    padding: 64px 0;
  }
  .clientes-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 560px) {
  .steps,
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .clientes-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .hero-actions .cta-button {
    width: 100%;
  }
  .footer-top {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .footer-links,
  .footer-contacts {
    align-items: center;
  }
  .footer-link {
    justify-content: center;
  }
  .footer-social {
    justify-content: center;
  }
  .footer-tagline {
    margin: 0 auto;
  }
}
</style>
